import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DetailedUploadCard from '@components/test-upload/DetailedUploadCard';
import InstructionsAlert from '@components/test-upload/InstructionsAlert';
import QuickUploadCard from '@components/test-upload/QuickUploadCard';
import UploadProgressCard from '@components/test-upload/UploadProgressCard';
import UploadResultsCard, { UploadResults } from '@components/test-upload/UploadResultsCard';
import { batchUploadTestData, quickUploadIncidentFiles } from '../utils/testDataUploader';

const TestUploadPage: React.FC = () => {
  const { t } = useTranslation();

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState('');
  const [uploadResults, setUploadResults] = useState<UploadResults | null>(null);
  const [category, setCategory] = useState('incident');
  const categoryOptions = [
    { value: 'incident', label: t('testUpload.categories.incident') },
    { value: 'finance', label: t('testUpload.categories.finance') },
    { value: 'planning', label: t('testUpload.categories.planning') },
    { value: 'analytics', label: t('testUpload.categories.analytics') },
    { value: 'engineering', label: t('testUpload.categories.engineering') },
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      setSelectedFiles(files);
      setUploadResults(null);
    }
  };

  const handleStartUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadResults(null);

    try {
      const result = await batchUploadTestData(selectedFiles, {
        t,
        category,
        tags: category === 'incident' ? ['過去トラ', 'インシデント'] : [],
        department: 'Operations',
        classification: 'internal',
        onProgress: (progress, fileName) => {
          setUploadProgress(progress);
          setCurrentFile(fileName);
        },
        onError: (error, fileName) => {
          console.error(`Failed to upload ${fileName}:`, error.message);
        },
        onSuccess: (fileName) => {
          console.log(`Successfully uploaded: ${fileName}`);
        },
      });

      setUploadResults(result);
    } catch (error) {
      console.error('Batch upload failed:', error);
    } finally {
      setUploading(false);
      setUploadProgress(100);
    }
  };

  const handleQuickIncidentUpload = () => {
    // インシデントファイル用のファイル選択ダイアログを開く
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf';

    input.addEventListener('change', async (event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const files = Array.from(target.files);
        setSelectedFiles(files);
        setCategory('incident');

        // 自動でアップロード開始
        setUploading(true);
        setUploadProgress(0);
        setUploadResults(null);

        try {
          await quickUploadIncidentFiles(files, t);

          const result = await batchUploadTestData(files, {
            t,
            category: 'incident',
            tags: ['過去トラ', 'インシデント', '対応済み'],
            department: 'Operations',
            classification: 'internal',
            onProgress: (progress, fileName) => {
              setUploadProgress(progress);
              setCurrentFile(fileName);
            },
          });

          setUploadResults(result);
        } catch (error) {
          console.error('Quick upload failed:', error);
        } finally {
          setUploading(false);
        }
      }
    });

    input.click();
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <header>
        <h1 className="revy-cjk text-2xl font-semibold leading-[1.4] tracking-tight">
          {t('testUpload.title')}
        </h1>
        <p className="revy-cjk mt-1 text-sm leading-[1.6] text-muted-foreground">
          {t('testUpload.description')} {t('testUpload.incidentDirectory')}
        </p>
      </header>

      <QuickUploadCard uploading={uploading} onSelectIncidentFiles={handleQuickIncidentUpload} />

      <DetailedUploadCard
        category={category}
        categoryOptions={categoryOptions}
        selectedFiles={selectedFiles}
        uploading={uploading}
        onCategoryChange={setCategory}
        onFileSelect={handleFileSelect}
        onStartUpload={handleStartUpload}
      />

      {uploading && <UploadProgressCard progress={uploadProgress} currentFile={currentFile} />}

      {uploadResults && <UploadResultsCard results={uploadResults} />}

      <InstructionsAlert />
    </div>
  );
};

export default TestUploadPage;
